import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
} from "react-bootstrap";
import statisticsService from "../../../components/Statistics/services/statisticsService";
import userService from "../../../components/User/services/userService";
import { showError } from "../../../Utilities/sweetAlert";
import { COLORS } from "../../constants/colors";
import "./Main.css";

interface MostHikedTrail {
  trailId: number;
  trailName: string;
  hikeCount: number;
}

interface MostSavedTrail {
  trailId: number;
  trailName: string;
  saveCount: number;
}

interface RouteStats {
  totalRoutes: number;
  byState: { draft: number; personal: number; public: number };
  byStatus: { pending: number; active: number; inactive: number };
  pendingReview: number;
}

const Main: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mostHikedTrail, setMostHikedTrail] = useState<MostHikedTrail | null>(null);
  const [mostSavedTrail, setMostSavedTrail] = useState<MostSavedTrail | null>(null);
  const [hikesCompleted, setHikesCompleted] = useState<number>(0);
  const [hikesStarted, setHikesStarted] = useState<number>(0);
  const [monthlySubscribers, setMonthlySubscribers] = useState<number>(0);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [activeUsers, setActiveUsers] = useState<number>(0);
  const [premiumUsers, setPremiumUsers] = useState<number>(0);
  const [signedUpUsers, setSignedUpUsers] = useState<number>(0);
  const [recentRegistrations, setRecentRegistrations] = useState<number>(0);
  const [routeStats, setRouteStats] = useState<RouteStats>({
    totalRoutes: 0,
    byState: { draft: 0, personal: 0, public: 0 },
    byStatus: { pending: 0, active: 0, inactive: 0 },
    pendingReview: 0,
  });

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        setError(null);

        const [
          mostHikedRes,
          mostSavedRes,
          completedRes,
          startedRes,
          subscribersRes,
          userStatsRes,
          routeStatsRes,
        ] = await Promise.all([
          statisticsService.getMostHikedTrail(),
          statisticsService.getMostSavedTrail(),
          statisticsService.getHikesCompletedCount(),
          statisticsService.getHikesStartedCount(),
          statisticsService.getMonthlySubscribersCount(),
          userService.getUserStats("all"),
          statisticsService.getRouteStats(),
        ]);

        setMostHikedTrail(mostHikedRes.data);
        setMostSavedTrail(mostSavedRes.data);
        setHikesCompleted(completedRes.data.count);
        setHikesStarted(startedRes.data.count);
        setMonthlySubscribers(subscribersRes.data.count);

        const stats = userStatsRes.data.stats;
        setTotalUsers(stats.totalUsers);
        setActiveUsers(stats.usersByStatus.active);
        setPremiumUsers(stats.premiumUsers);
        setSignedUpUsers(stats.signedUpUsers);
        setRecentRegistrations(stats.recentRegistrations);

        setRouteStats(routeStatsRes.data);
      } catch (err: any) {
        console.error("Error fetching statistics:", err);
        setError("Failed to fetch statistics");
        showError("Failed to fetch statistics");
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  const premiumConversionRate = signedUpUsers > 0 
    ? ((premiumUsers / signedUpUsers) * 100).toFixed(1) 
    : "0.0";

  return (
    <div
      className="Body"
      style={{
        backgroundSize: "50px",
      }}
    >
      {loading ? (
        <Container fluid className="py-4">
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-2">Loading dashboard statistics...</p>
          </div>
        </Container>
      ) : error ? (
        <Container fluid className="py-4">
          <Alert variant="danger">{error}</Alert>
        </Container>
      ) : (
        <Container fluid className="py-4">
          <Row className="mb-4">
            <Col>
              <h2 className="dashboard-title" style={{ color: COLORS.TERTIARY_DARK }}>
                Dashboard Overview
              </h2>
            </Col>
          </Row>

          {/* User Statistics */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.TERTIARY_DARK }}>
                    {totalUsers.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Total Users</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.BUTTON_GREEN }}>
                    {activeUsers.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Active Users</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.PRIMARY_ORANGE }}>
                    {premiumUsers.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Premium Users</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.SECONDARY_GREEN }}>
                    {recentRegistrations.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">New Users (7 days)</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Business Metrics */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.BUTTON_GREEN }}>
                    {hikesCompleted.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Hikes Completed</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.PRIMARY_ORANGE }}>
                    {hikesStarted.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Hikes Started</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.SECONDARY_GREEN }}>
                    {monthlySubscribers.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Monthly Subscribers</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.MEDIUM_GREY }}>
                    {premiumConversionRate}%
                  </div>
                  <div className="dashboard-stat-label">Premium Conversion</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* User Routes Statistics */}
          <Row className="mb-3">
            <Col>
              <h5 style={{ color: COLORS.TERTIARY_DARK, fontWeight: 600 }}>User Routes</h5>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col md={3}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.TERTIARY_DARK }}>
                    {routeStats.totalRoutes.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Total Routes</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.PRIMARY_ORANGE }}>
                    {routeStats.pendingReview.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Pending Review</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.BUTTON_GREEN }}>
                    {routeStats.byStatus.active.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Approved</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.ERROR_RED }}>
                    {routeStats.byStatus.inactive.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Rejected</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col md={4}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.MEDIUM_GREY }}>
                    {routeStats.byState.draft.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Draft Routes</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.SECONDARY_GREEN }}>
                    {routeStats.byState.personal.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Personal Routes</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="dashboard-stat-card">
                <Card.Body className="text-center">
                  <div className="dashboard-stat-number" style={{ color: COLORS.BUTTON_GREEN }}>
                    {routeStats.byState.public.toLocaleString()}
                  </div>
                  <div className="dashboard-stat-label">Public Routes</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Most Hiked and Most Saved Trails */}
          <Row>
            <Col md={6}>
              <Card className="dashboard-trail-card">
                <Card.Header style={{ backgroundColor: COLORS.PRIMARY_ORANGE, color: COLORS.WHITE }}>
                  <h5 className="mb-0" style={{ color: COLORS.WHITE }}>Most Hiked Trail</h5>
                </Card.Header>
                <Card.Body>
                  {mostHikedTrail ? (
                    <div>
                      <h4 style={{ color: COLORS.TERTIARY_DARK }}>{mostHikedTrail.trailName}</h4>
                      <p className="text-muted mb-0">
                        <strong>{mostHikedTrail.hikeCount}</strong> hike{mostHikedTrail.hikeCount !== 1 ? "s" : ""}
                      </p>
                    </div>
                  ) : (
                    <p className="text-muted mb-0">No hikes found</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="dashboard-trail-card">
                <Card.Header style={{ backgroundColor: COLORS.SECONDARY_GREEN, color: COLORS.WHITE }}>
                  <h5 className="mb-0" style={{ color: COLORS.WHITE }}>Most Saved Trail</h5>
                </Card.Header>
                <Card.Body>
                  {mostSavedTrail ? (
                    <div>
                      <h4 style={{ color: COLORS.TERTIARY_DARK }}>{mostSavedTrail.trailName}</h4>
                      <p className="text-muted mb-0">
                        <strong>{mostSavedTrail.saveCount}</strong> user{mostSavedTrail.saveCount !== 1 ? "s" : ""} saved this trail
                      </p>
                    </div>
                  ) : (
                    <p className="text-muted mb-0">No saved trails found</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      )}
    </div>
  );
};

export default Main;
